<template>
  <div>
    <!-- Offcanvas Menu -->
    <div class="offcanvas offcanvas-start" tabindex="-1" id="mobileMenu" aria-labelledby="mobileMenuLabel">
      <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="mobileMenuLabel">Menu</h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
          <div class="mb-4">
              <search-box />
              <router-link class="offcanvas-menu-item" to="/">
                  <font-awesome-icon class="offcanvas-icon" icon="fas fa-home" />
                  Home
              </router-link>
              <router-link class="offcanvas-menu-item" to="/search">
                  <font-awesome-icon icon="fas fa-store" />
                  Shop
              </router-link>
              <router-link class="offcanvas-menu-item" to="/compare">
                  <font-awesome-icon icon="fas fa-scale-balanced" />
                  Compare
                  <span
                    v-if="compareItemCount"
                    class="badge bg-danger ms-2"
                  >
                    {{ compareItemCount }}
                  </span>
              </router-link>
              <!-- <router-link class="offcanvas-menu-item" to="/blogs">
                  <font-awesome-icon icon="fas fa-newspaper" />
                  Blogs
              </router-link> -->
              <router-link class="offcanvas-menu-item" to="/orders">
                <font-awesome-icon icon="fas fa-shopping-bag" />
                My Orders
              </router-link>
              <router-link class="offcanvas-menu-item" :to="{ path: '/contact' }">
                  <font-awesome-icon icon="fas fa-envelope" />
                  Contact
              </router-link>
              <router-link
                v-if="isLoggedIn"
                class="offcanvas-menu-item"
                to="#"
                @click.prevent="logout"
              >
                <font-awesome-icon icon="fa-solid fa-right-from-bracket" />
                Logout
              </router-link>
          </div>

          <div class="border-top pt-4">
              <h6 class="mb-3 ps-3">Categories</h6>
              <a
                v-for="(category, index) in categories"
                :key="`category-${index}`"
                :href="'/search/?c=' + category.slug"
                class="offcanvas-menu-item"
              >
                <span class="category-text">
                  <i :class="category.icon" class="category-icon"></i>
                  {{ category.name }}
                </span>
              </a>
          </div>

          <div class="border-top pt-4">
              <h6 class="mb-3 ps-3">Brands</h6>
              <div
                v-for="(group, gi) in brandGroups"
                :key="`brand-group-${gi}`"
              >
                <h6 class="ps-3 mt-2 mb-1 text-uppercase small text-muted">
                  {{ group.title }}
                </h6>
                <a
                  v-for="(brand, bi) in group.brands"
                  :key="`brand-${gi}-${bi}`"
                  :href="'/brand/' + brand.replace(/ /g, '_')"
                  class="offcanvas-menu-item"
                >
                  <span class="category-text">{{ brand }}</span>
                </a>
              </div>
          </div>
      </div>
  </div>

    <!-- Header -->
    <nav class="navbar navbar-expand-lg header fixed-top navbar-bg-custom">
      <div class="container px-3">

        <!-- Top row for mobile -->
        <div class="d-flex w-100 d-lg-none justify-content-between align-items-center">
          <!-- Toggler (left) -->
          <button class="mobile-menu-toggle" data-bs-toggle="offcanvas" data-bs-target="#mobileMenu">
            <font-awesome-icon icon="fas fa-bars" />
          </button>

          <!-- Centered brand on mobile -->
          <a class="navbar-brand position-absolute start-50 translate-middle-x logo" href="/">
            <img src="../assets/sehrish-logo-2.png" alt="Store Logo" width="30" height="30">
            <!-- Sehrish Mart -->
          </a>

          <!-- Right-side icons (mobile): wishlist, cart, account -->
          <div class="mobile-icons d-flex align-items-center">
            <router-link class="icon-link" to="/wishlist">
              <i class="bi bi-heart"></i>
              <span v-if="wishlistItemCount" class="badge-count position-absolute top-0 start-100 translate-middle d-flex align-items-center justify-content-center badge bg-danger">
                {{ wishlistItemCount }}
              </span>
            </router-link>
            <router-link class="icon-link" to="/cart">
              <i class="bi bi-cart"></i>
              <span v-if="cartItemCount" class="badge-count position-absolute top-0 start-100 translate-middle d-flex align-items-center justify-content-center badge bg-danger">
                {{ cartItemCount }}
              </span>
            </router-link>
            <router-link class="icon-link" to="/profile">
              <i class="bi bi-person"></i>
            </router-link>
          </div>
        </div>

        <!-- Search box on mobile (its own row, below the top row) -->
        <div class="d-flex d-lg-none w-100 mt-3">
          <form @submit.prevent="goToSearchPage" class="search-box w-100">
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search products..."
              @blur="showSuggestions = false"
              @input="debounceSearch"
            />
            <font-awesome-icon
              class="search-icon"
              icon="fas fa-search"
              @click="goToSearchPage"
            />
            <div class="search-result-wrapper">
              <ul
                v-if="
                  searchSuggestions.length &&
                  searchQuery.trim() &&
                  showSuggestions
                "
                class="search-dropdown"
              >
                <li
                  v-for="(product, b) in searchSuggestions"
                  :key="`search-mobile-${b}`"
                  @mousedown.prevent="selectResult(product)"
                >
                  <img
                    :src="
                      product.images?.length
                        ? `${BASE_IMAGE_PREFIX}/${product.images[0]}`
                        : `${PLACEHOLDER_IMAGE}`
                    "
                    class="suggestion-product-image"
                    alt="Product image"
                  />
                  <span>{{ product.title }}</span>
                </li>
              </ul>
            </div>
          </form>
        </div>

        <!-- Row on desktop: logo + search box + support info -->
        <div class="d-none d-lg-flex align-items-center justify-content-between w-100">
          <a class="navbar-brand me-4 logo" href="/">
            <img src="../assets/sehrish-logo-2.png" alt="Store Logo" width="30" height="30">
            <!-- Sehrish mart -->
          </a>

          <!-- Search box (moved here from the search section) -->
          <form @submit.prevent="goToSearchPage" class="search-box flex-grow-1 mx-4">
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search products..."
              @blur="showSuggestions = false"
              @input="debounceSearch"
            />
            <font-awesome-icon
              class="search-icon"
              icon="fas fa-search"
              @click="goToSearchPage"
            />
            <!-- Dropdown for search results -->
            <div class="search-result-wrapper">
              <ul
                v-if="
                  searchSuggestions.length &&
                  searchQuery.trim() &&
                  showSuggestions
                "
                class="search-dropdown"
              >
                <li
                  v-for="(product, b) in searchSuggestions"
                  :key="`search-${b}`"
                  @mousedown.prevent="selectResult(product)"
                >
                  <img
                    :src="
                      product.images?.length
                        ? `${BASE_IMAGE_PREFIX}/${product.images[0]}`
                        : `${PLACEHOLDER_IMAGE}`
                    "
                    class="suggestion-product-image"
                    alt="Product image"
                  />
                  <span>{{ product.title }}</span>
                </li>
              </ul>
            </div>
          </form>

          <!-- Support info -->
          <div class="support-info">
            <span class="support-icon">
              <a href="tel:+8801882124009">
                <font-awesome-icon icon="fa fa-phone" />
              </a>
            </span>
            <div class="support-text-wrap">
              <div class="support-label">Support</div>
              <a href="tel:+8801882124009" class="support-number">+880 1882 124 009</a>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!--
      Request a Product Modal — not part of the current visual design, but kept
      here (commented out) so it can be re-enabled later. Re-add a trigger
      (e.g. a button) anywhere and set `showRequestModal = true` to reuse it.
    -->
    <!--
    <div
      v-if="showRequestModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div v-if="!isLoggedIn" class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Please login first.</h5>
            <button
              type="button"
              class="btn-close"
              @click="showRequestModal = false"
            ></button>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="goToLogin">
              Login
            </button>
          </div>
        </div>
        <div v-else class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Request a Product</h5>
            <button
              type="button"
              class="btn-close"
              @click="showRequestModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <textarea
              v-model="requestedProduct"
              v-if="!requestSuccess"
              class="form-control"
              placeholder="Product name, brand etc."
            ></textarea>
            <div v-if="requestSuccess" class="d-block p-5 text-center">
              <h5>Your request is successfully placed.</h5>
            </div>
          </div>
          <div v-if="!requestSuccess" class="modal-footer">
            <button class="btn btn-secondary" @click="showRequestModal = false">
              Cancel
            </button>
            <button class="btn btn-primary" @click="requestProduct">
              Send Request
            </button>
          </div>
        </div>
      </div>
    </div>
    -->
  </div>
</template>

<script>
import axios from "axios";
import SearchBox from "./SearchBox.vue";
import { BASE_URL, BASE_IMAGE_PREFIX, PLACEHOLDER_IMAGE } from "@/globals";
import { analytics } from "@/services/analytics";

export default {
  components: { SearchBox },
  name: "HeaderComponent",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      PLACEHOLDER_IMAGE,

      // Search box state (moved here from the old search section)
      searchQuery: null,
      searchTimeout: null,
      searchSuggestions: [],
      showSuggestions: false,

      categories: [
        {
          name: "Cleansers",
          icon: "bi bi-droplet",
          slug: "cleansers",
        },
        {
          name: "Face Serums",
          icon: "bi bi-eyedropper",
          slug: "face_serums",
        },
        {
          name: "Moisturizer",
          icon: "bi bi-moisture",
          slug: "moisturizer",
        },
        {
          name: "Sunscreen",
          icon: "bi bi-sun",
          slug: "sunscreen",
        },
        {
          name: "Toners",
          icon: "bi bi-water",
          slug: "toners",
        },
        {
          name: "Face Masks",
          icon: "bi bi-stars",
          slug: "face_masks",
        },
        {
          name: "Acne Care",
          icon: "bi bi-shield-check",
          slug: "acne_care",
        },
        {
          name: "Anti-Aging",
          icon: "bi bi-hourglass-split",
          slug: "anti_aging",
        },
        {
          name: "Body Care",
          icon: "bi bi-heart",
          slug: "body_care",
        },
        {
          name: "Lip Care",
          icon: "bi bi-emoji-kiss",
          slug: "lip_care",
        },
      ],

      brandGroups: [
        {
          title: "Korean Brands",
          brands: [
            "Abib",
            "Anua",
            "APLB",
            "April Skin",
            "Arencia",
            "Aromatica",
            "AXIS-Y",
            "Banila Co.",
            "Beauty of Joseon",
            "BRINGGREEN",
            "Celimax",
            "Cos De BAHA",
            "COSRX",
            "Dr. Althea",
            "Dr. Ceuracle",
            "Eqqualberry",
            "Etude House",
            "FINO",
            "FULLY",
            "Goodal",
            "HaruHaru Wonder",
            "Heimish",
            "Heveblue",
            "House of Hur",
            "Illiyoon",
            "I'M FROM",
            "Innisfree",
            "Isntree",
            "Jumiso",
            "KAINE",
            "KSECRET",
            "Laneige",
            "lunik",
            "Manyo",
            "Mary & May",
            "Medicube",
            "Missha",
            "Mixsoon",
            "Nature Republic",
            "Neogen Dermalogy",
            "Nineless",
            "Numbuzin",
            "Purito",
            "Pyunkang Yul",
            "Round Lab",
            "Rovectin",
            "Some By Mi",
            "The Face Sop",
            "Tiam",
            "TIRTIR",
            "Tocobo",
            "Torriden",
            "VT Cosmetics",
          ],
        },
        {
          title: "USA/UK/France Brands",
          brands: [
            "Bath & Body Works",
            "Bioderma",
            "Cerave",
            "Cetaphil",
            "Differin",
            "La Roche Posay",
            "Mielle",
            "Neutrogena",
            "PanOxyl",
            "Paula's Choice",
            "The Ordinary",
          ],
        },
      ],

      // --- Request a Product feature: hidden in the current design, kept for later ---
      // showRequestModal: false,
      // requestedProduct: null,
      // errorMessage: null,
      // requestSuccess: false,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    cartItemCount() {
      return this.$store.getters.cartItemCount;
    },
    wishlistItemCount() {
      return this.$store.getters.wishlistItemCount;
    },
    compareItemCount() {
      return this.$store.getters.compareItemCount;
    },
  },
  methods: {
    goToSearchPage() {
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        return;
      }
      this.$router.push(`/search?b=${encodeURIComponent(this.searchQuery)}`);
    },
    async fetchSuggestions() {
      try {
        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            q: this.searchQuery?.trim() || undefined,
            category: undefined,
            brand: undefined,
            limit: 3,
            hasActiveVariant: true,
          },
        });

        this.searchSuggestions = response.data.products;
        this.showSuggestions = true;
        analytics.search(this.searchQuery);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        this.isLoading = false;
      }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      if (this.searchQuery.trim()) {
        this.searchTimeout = setTimeout(() => {
          this.fetchSuggestions();
        }, 500);
      }
    },
    selectResult(product) {
      this.$router.push(`/product/${product._id}`);
    },
    logout() {
      this.$store.dispatch("logout");
      window.location.reload();
    },
  },
};
</script>

<style lang='scss'>
.navbar-bg-custom {
  background-color: #fff;
}
/* ################ */
/* Adding new styles for mobile menu */
.mobile-menu-toggle {
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  padding: 5px;
}

.offcanvas {
  max-width: 300px;
}

.offcanvas-menu-item {
  display: flex;
  align-items: center;
  padding: 12px 20px;
  color: #333;
  text-decoration: none;
  border-bottom: 1px solid #eee;
  transition: all 0.3s;
}

.offcanvas-menu-item:hover {
  background-color: #f8f9fa;
  color: var(--primary-color);
}

.offcanvas-menu-item svg {
  margin-right: 12px;
  width: 20px;
  text-align: center;
}

.category-text {
  font-size: 15px;
  font-weight: 400;
}

.category-icon {
  margin-right: 5px;
  opacity: 0.5;
  font-size: 18px;
}

.category-link:hover .category-icon {
  opacity: 1;
}

/* ############## */
/* Top Banner */

.top-banner {
  background-color: var(--secondary-color);
  padding: 10px 0;
  font-size: 12px;
  font-weight: 300;
  color: white;
}

.top-banner a {
  color: white;
  text-decoration: none;
}

.social-icons a {
  margin-left: 15px;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.social-icons a:hover {
  opacity: 1;
}

/* ################## */
/* Header */

// .header {
//   padding: 15px 0;
// }

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 24px;
  font-weight: 700;
  color: #333;
  text-decoration: none;
  flex-shrink: 0;
  img {
      width: 100px;
      height: auto;
    }
}

.logo:hover {
  color: var(--primary-color);
}

/* Search box (moved here from the search section) */
.search-box {
  position: relative;
  max-width: 600px;
  .search-icon {
    position: absolute;
    top: 50%;
    right: 22px;
    transform: translateY(-50%);
    font-size: 18px;
    color: #888;
    cursor: pointer;
  }
  .search-icon:hover {
    color: var(--primary-color);
  }
}

.search-input {
  border-radius: 8px;
  padding: 12px 25px;
  border: 1px solid #e2e2e2;
  width: 100%;
  background-color: #fff;
}

.search-result-wrapper {
  position: relative;
  .search-dropdown {
    position: absolute;
    width: 100%;
    background: white;
    border: 1px solid #ddd;
    border-radius: 10px;
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 5px 0 0 0;
    z-index: 5;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .search-dropdown li {
    padding: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    .suggestion-product-image {
      width: auto;
      height: 40px;
      border-radius: 5px;
      margin-right: 10px;
      object-fit: cover;
    }
  }
  .search-dropdown li:hover {
    background: #f0f0f0;
  }
}

.support-info {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.support-icon {
  width: 42px;
  height: 42px;
  display: flex;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #cfcfcf;
  background-color: transparent;
  margin-right: 12px;
  font-size: 16px;
  a {
    color: #333;
    display: flex;
  }
}

.support-text-wrap {
  line-height: 1.3;
}

.support-label {
  font-size: 13px;
  color: #888;
  font-weight: 400;
}

.support-number {
  color: #222;
  font-weight: 700;
  font-size: 16px;
  text-decoration: none;
}

.wishlist-count {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  font-size: 10px;
}

.mobile-icons {
  gap: 18px;

  .icon-link {
    position: relative;
    color: #333;
    font-size: 20px;
    text-decoration: none;
  }

  .badge-count {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    font-size: 10px;
  }
}

/* #################### */
/* Responsive Adjustments */
@media (max-width: 991px) {
  .mobile-menu-toggle {
      display: block;
  }
}

@media (max-width: 767px) {
  .logo {
    font-size: 20px;
    // img {
    //   width: 60px;
    //   height: auto;
    // }
  }
  .top-banner .notice {
        display: none;
    }
}
</style>